// Doubao flash protocol mirrors redraw/subtitle_realign/asr.py. Full episode
// audio is extracted without seeking, so ASR milliseconds stay video-relative.
import { execFile } from "node:child_process";
import { promisify } from "node:util";
import { createHash, randomUUID } from "node:crypto";
import * as fs from "node:fs/promises";
import * as path from "node:path";
import { z } from "zod";
import { post } from "./net.js";

const exec = promisify(execFile);
const timedTextSchema = z.object({
  text: z.string().min(1),
  start_time: z.number().int().nonnegative(),
  end_time: z.number().int().nonnegative(),
}).refine((u) => u.end_time >= u.start_time, "ASR 结束时间早于开始时间");
// Doubao emits whitespace separators with -1 times; they are not spoken words.
const wordsSchema = z.array(z.object({
  text: z.string(), start_time: z.number(), end_time: z.number(),
})).transform((words) => words.filter((word) => word.text.trim().length > 0))
  .pipe(z.array(timedTextSchema));
const utteranceSchema = timedTextSchema.safeExtend({ words: wordsSchema.optional() });
const responseSchema = z.object({ result: z.object({ utterances: z.array(utteranceSchema) }) });
export type AsrUtterance = z.infer<typeof utteranceSchema>;

export async function hasAudioTrack(video: string): Promise<boolean> {
  const { stdout } = await exec("ffprobe", ["-v", "error", "-select_streams", "a:0",
    "-show_entries", "stream=index", "-of", "csv=p=0", video], { timeout: 30_000 });
  return stdout.trim().length > 0;
}

export function parseAsr(raw: unknown): AsrUtterance[] {
  return responseSchema.parse(raw).result.utterances;
}

export function asrBody(audio: Buffer) {
  return {
    user: { uid: "scriptctl-review" },
    audio: { data: audio.toString("base64"), format: "mp3" },
    request: {
      model_name: "bigmodel", show_utterances: true,
      enable_itn: false, enable_punc: false, enable_ddc: false,
      vad_segment: true, end_window_size: 800,
    },
  };
}

export function timeLabel(ms: number): string {
  const minutes = Math.floor(ms / 60_000);
  const seconds = Math.floor(ms / 1000) % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}.${String(ms % 1000).padStart(3, "0")}`;
}

export interface AsrCue { id: string; text: string; start_time: number; end_time: number }

/** Stable references let Gemini select native ASR times rather than invent them. */
export function asrCues(utterances: AsrUtterance[]): AsrCue[] {
  return utterances.flatMap((u, i) => [
    { id: `U${i + 1}`, text: u.text, start_time: u.start_time, end_time: u.end_time },
    ...(u.words ?? []).map((word, j) => ({ id: `U${i + 1}W${j + 1}`, ...word })),
  ]);
}

export function asrText(utterances: AsrUtterance[]): string {
  return asrCues(utterances).map((u) => `[${timeLabel(u.start_time)}–${timeLabel(u.end_time)}] ${u.id} ${u.text}`).join("\n");
}

export async function episodeAsr(video: string, evidenceDir: string): Promise<AsrUtterance[]> {
  const apiKey = process.env.SD_AUDIO_APIKEY?.trim();
  if (!apiKey) throw new Error("全剧 ASR 缺少 SD_AUDIO_APIKEY，尚未启动剧本审核");
  await fs.mkdir(evidenceDir, { recursive: true });
  const temp = await fs.mkdtemp(path.join(evidenceDir, "audio-"));
  const audioPath = path.join(temp, "episode.mp3");
  let audio: Buffer;
  try {
    await exec("ffmpeg", ["-nostdin", "-v", "error", "-i", video,
      "-map", "0:a:0", "-vn", "-ac", "1", "-ar", "16000", "-b:a", "64k", audioPath],
    { timeout: 120_000 });
    audio = await fs.readFile(audioPath);
  } finally {
    await fs.rm(temp, { recursive: true, force: true });
  }
  const hash = createHash("sha256").update(audio).digest("hex");
  const cache = path.join(evidenceDir, `doubao-flash-v1-${hash}.json`);
  // Only a complete successful ASR response is reusable; unknown submissions
  // are never retried automatically. Invalid cache data fails explicitly.
  const cached = await fs.readdir(evidenceDir);
  if (cached.includes(path.basename(cache))) return parseAsr(JSON.parse(await fs.readFile(cache, "utf8")));
  const response = await post("https://openspeech.bytedance.com/api/v3/auc/bigmodel/recognize/flash",
    asrBody(audio), 600_000, {
      "X-Api-Key": apiKey, "X-Api-Resource-Id": "volc.bigasr.auc_turbo",
      "X-Api-Request-Id": randomUUID(), "X-Api-Sequence": "-1",
    });
  const raw = await response.text();
  const rawPath = path.join(evidenceDir, `asr-response-${randomUUID()}.json`);
  await fs.writeFile(rawPath, raw);
  const status = response.headers.get("x-api-status-code");
  if (!response.ok || (status !== null && status !== "20000000")) {
    throw new Error(`豆包 ASR 失败：HTTP ${response.status}，${status ?? "无状态码"}，${response.headers.get("x-api-message") ?? ""}；原始响应：${rawPath}`);
  }
  const utterances = parseAsr(JSON.parse(raw));
  await fs.rename(rawPath, cache);
  return utterances;
}
